/**
 * Bookmarklet Content Component
 * Client component for bookmarklet page with interactive elements
 */

'use client';

import Link from 'next/link';

// Bookmarklet code
const bookmarkletCode = `javascript:(function(){var u=encodeURIComponent(location.href);window.open('https://caniusemyfsa.com/results?q='+u+'&mode=FSA','_blank')})();`;

export default function BookmarkletContent() {
  const handleCopy = () => {
    navigator.clipboard.writeText(bookmarkletCode);
    alert('Copied to clipboard!');
  };

  const handleDragInfo = (e: React.MouseEvent) => {
    e.preventDefault();
    alert('Drag this button to your bookmarks bar, or right-click and choose "Bookmark This Link"');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-primary-50 py-16 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12 animate-fade-in">
          <img 
            src="/logo.png" 
            alt="Can I Use My FSA" 
            className="w-20 h-20 mx-auto mb-4 rounded-2xl shadow-lg"
          />
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            FSA/HSA Checker Bookmarklet
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Check FSA/HSA eligibility on any product page with one click. 
            Works on <strong>mobile and desktop</strong> browsers!
          </p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 mb-8">
          {/* Bookmarklet Button */}
          <div className="text-center mb-8">
            <p className="text-gray-700 mb-4 font-medium">
              👇 Drag this button to your bookmarks bar:
            </p>
            <a
              href={bookmarkletCode}
              onClick={handleDragInfo}
              className="inline-flex items-center gap-3 bg-gradient-to-r from-primary-500 to-primary-600 text-white px-8 py-4 rounded-2xl font-bold text-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all cursor-move"
              title="Drag to bookmarks bar"
            >
              <img src="/logo.png" alt="" className="w-7 h-7 rounded-lg" />
              <span>Check FSA/HSA</span>
            </a>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-4 my-8">
            <div className="flex-1 h-px bg-gray-200"></div>
            <span className="text-gray-500 text-sm font-medium">OR</span>
            <div className="flex-1 h-px bg-gray-200"></div>
          </div>

          {/* Manual Installation */}
          <div className="bg-gray-50 rounded-xl p-6">
            <h3 className="font-bold text-gray-900 mb-4">📱 Mobile Installation</h3>
            <ol className="space-y-3 text-gray-700">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-primary-500 text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
                <span>Copy the code below</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-primary-500 text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
                <span>Create a new bookmark (bookmark any page first)</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-primary-500 text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
                <span>Edit the bookmark and replace the URL with the code</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-primary-500 text-white rounded-full flex items-center justify-center text-sm font-bold">4</span>
                <span>Rename it to &quot;Check FSA/HSA&quot;</span>
              </li>
            </ol>

            {/* Code Box */}
            <div className="mt-4 relative">
              <pre className="bg-gray-900 text-green-400 p-4 rounded-lg text-xs overflow-x-auto">
                <code>{bookmarkletCode}</code>
              </pre>
              <button
                onClick={handleCopy}
                className="absolute top-2 right-2 bg-gray-700 text-white px-3 py-1 rounded text-xs hover:bg-gray-600 transition-colors"
              >
                Copy
              </button>
            </div>
          </div>
        </div>

        {/* How It Works */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-4xl mb-3">🛒</div>
              <h3 className="font-bold text-gray-900 mb-2">1. Browse Products</h3>
              <p className="text-gray-600 text-sm">
                Shop on Amazon, Walmart, CVS, or any website
              </p>
            </div>
            <div className="text-center">
              <div className="text-4xl mb-3">🔖</div>
              <h3 className="font-bold text-gray-900 mb-2">2. Click Bookmarklet</h3>
              <p className="text-gray-600 text-sm">
                Tap the bookmarklet while on a product page
              </p>
            </div>
            <div className="text-center">
              <div className="text-4xl mb-3">✅</div>
              <h3 className="font-bold text-gray-900 mb-2">3. See Results</h3>
              <p className="text-gray-600 text-sm">
                Instantly know if it&apos;s FSA/HSA eligible
              </p>
            </div>
          </div>
        </div>

        {/* Browser Compatibility */}
        <div className="bg-gradient-to-r from-primary-50 to-primary-100 rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">
            ✅ Works On All Browsers
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            {[
              { name: 'Chrome', icon: '🌐' },
              { name: 'Safari', icon: '🧭' },
              { name: 'Firefox', icon: '🦊' },
              { name: 'Edge', icon: '📘' },
              { name: 'iOS Safari', icon: '📱' },
              { name: 'Android Chrome', icon: '🤖' },
            ].map((browser) => (
              <div
                key={browser.name}
                className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm"
              >
                <span>{browser.icon}</span>
                <span className="font-medium text-gray-700">{browser.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* PWA CTA */}
        <div className="mt-8 text-center">
          <p className="text-gray-600 mb-4">
            Want an even better mobile experience?
          </p>
          <Link
            href="/"
            className="inline-flex items-center gap-2 text-primary-600 font-semibold hover:text-primary-700"
          >
            <span>📲 Add to Home Screen for app-like experience</span>
            <span>→</span>
          </Link>
        </div>
      </div>
    </div>
  );
}

